import type { Client, PresenceHeartbeat } from "@khazzin/api-client";
import { type StreamEvent, type StreamStatus, TOPIC_RESYNC } from "@khazzin/api-client/stream";
import { onlineManager, type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { ConnectionStatus } from "./page";

/**
 * The live stream in React (EP10-S22): each topic invalidates the query keys a
 * module declares, a resync re-reads everything, and while the stream is not
 * open every open query is re-read every 60 s (`10/05` §8).
 */

/** The poll behind a stream that is down (`10/05` §8). */
export const FALLBACK_POLL_MS = 60_000;

/** The presence heartbeat's interval (`10/05` §3). */
export const HEARTBEAT_MS = 60_000;

/** The query keys each topic invalidates, declared by the app per module. */
export type TopicKeys = Readonly<Record<string, readonly QueryKey[]>>;

export interface UseEventStreamOptions {
	/** False while there is no session to stream for. */
	enabled?: boolean;
	/** Reconnects when it changes: the Store Portal's store. */
	scope?: string;
	/** Anything beyond invalidation, such as the Driver App's sync. */
	onEvent?: (event: StreamEvent) => void;
}

/**
 * Streams while enabled, invalidating each topic's query keys and every query
 * on a resync, and re-reads the open queries every 60 s while the stream is
 * not open. Answers the stream's state for the connection pill.
 */
export function useEventStream(
	api: Client | null,
	topics: TopicKeys,
	{ enabled = true, scope = "", onEvent }: UseEventStreamOptions = {},
): StreamStatus {
	const queries = useQueryClient();
	const [status, setStatus] = useState<StreamStatus>("idle");
	const latest = useRef({ topics, onEvent });
	latest.current = { topics, onEvent };

	// biome-ignore lint/correctness/useExhaustiveDependencies: the scope is the trigger (a store switch is a new stream), not a value read here.
	useEffect(() => {
		if (api === null || !enabled) {
			setStatus("idle");
			return;
		}
		const close = api.streamEvents({
			onStatus: setStatus,
			onEvent: (event) => {
				if (event.topic === TOPIC_RESYNC) {
					void queries.invalidateQueries();
				}
				for (const queryKey of latest.current.topics[event.topic] ?? []) {
					void queries.invalidateQueries({ queryKey });
				}
				latest.current.onEvent?.(event);
			},
		});
		return () => {
			close();
			setStatus("idle");
		};
	}, [api, enabled, scope, queries]);

	useEffect(() => {
		if (status === "open" || !enabled) {
			return;
		}
		const timer = setInterval(
			() => void queries.invalidateQueries({ type: "active" }),
			FALLBACK_POLL_MS,
		);
		return () => clearInterval(timer);
	}, [status, enabled, queries]);

	return status;
}

/**
 * The pill's state for an app without a session poll (the Store Portal and
 * the Driver App): the browser's network, then the stream.
 */
export function useStreamConnection(stream: StreamStatus): ConnectionStatus {
	const [online, setOnline] = useState(() => onlineManager.isOnline());
	useEffect(() => onlineManager.subscribe(setOnline), []);
	if (!online) {
		return "offline";
	}
	return stream === "error" ? "reconnecting" : "live";
}

/**
 * Sends a presence heartbeat now and every 60 s while the page is visible,
 * and again the moment it becomes visible. A failed heartbeat is dropped: the
 * next one is a minute away.
 */
export function usePresenceHeartbeat(
	api: Client | null,
	heartbeat: () => PresenceHeartbeat,
	enabled = true,
): void {
	const latest = useRef(heartbeat);
	latest.current = heartbeat;

	useEffect(() => {
		if (api === null || !enabled) {
			return;
		}
		let timer: ReturnType<typeof setInterval> | null = null;
		const beat = () => void api.presenceHeartbeat(latest.current()).catch(() => undefined);
		const sync = () => {
			if (timer !== null) {
				clearInterval(timer);
				timer = null;
			}
			if (document.visibilityState === "visible") {
				beat();
				timer = setInterval(beat, HEARTBEAT_MS);
			}
		};
		sync();
		document.addEventListener("visibilitychange", sync);
		return () => {
			document.removeEventListener("visibilitychange", sync);
			if (timer !== null) {
				clearInterval(timer);
			}
		};
	}, [api, enabled]);
}
